import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ArrowLeft, GoogleLogo, Phone } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminEmailHint, setAdminEmailHint] = useState('');

  // Point 7 — Google re-verification OR a 6-digit emailed OTP, admin's choice.
  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleContinue = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim()) {
      setError('Please enter your admin phone number.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/request-password-reset`, {
        phone: phone.trim(),
      });

      if (!data.temp_token) {
        // Backend returns generic message when admin doesn't exist — don't leak
        setError('If that admin account exists, we would proceed with Google verification. Please double-check your phone number.');
        setLoading(false);
        return;
      }

      // Persist reset context before redirecting to Google
      sessionStorage.setItem('admin_pwreset_temp_token', data.temp_token);
      sessionStorage.setItem('admin_pwreset_phone', phone.trim());
      setAdminEmailHint(data.admin_email || '');

      const redirectUrl = window.location.origin + '/admin-password-reset-callback';
      window.location.href =
        `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start password reset. Please try again.');
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError('');
    if (!phone.trim()) {
      setError('Please enter your admin phone number.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-password-reset`, null, {
        params: { phone: phone.trim() },
      });
      setOtpEmailHint(data.email_hint || '');
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim() || otp.trim().length !== 6) {
      setError('Enter the 6-digit OTP sent to your email.');
      return;
    }
    if (!newPassword || newPassword.length < 5) {
      setError('New password must be at least 5 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp-reset-password`, null, {
        params: { phone: phone.trim(), otp: otp.trim(), new_password: newPassword },
      });
      navigate('/admin-login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center p-4"
      style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <button
          type="button"
          onClick={() => navigate('/admin-login')}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 mb-6"
          data-testid="admin-forgot-back-btn"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to login
        </button>

        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
            <ShieldCheck size={40} weight="fill" className="text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Admin Password</h1>
          <p className="text-sm text-gray-500 mt-2">
            We'll verify your identity with Google before letting you set a new password.
          </p>
        </div>

        {!otpMode && (
          <form onSubmit={handleContinue} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Phone Number
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter admin phone number"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="admin-forgot-phone-input"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="admin-forgot-error">
                {error}
              </div>
            )}

            {adminEmailHint && (
              <div className="bg-purple-50 border border-purple-200 text-purple-800 px-3 py-2 rounded-lg text-xs">
                You'll be asked to sign in as <strong>{adminEmailHint}</strong> on the next screen.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="admin-forgot-verify-btn"
            >
              <GoogleLogo size={20} weight="bold" />
              {loading ? 'Redirecting to Google...' : 'Verify with Google'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              Only the authorized admin Gmail can reset this password.
            </p>

            <button
              type="button"
              onClick={() => { setError(''); setOtpMode(true); }}
              className="w-full text-center text-sm text-purple-700 hover:underline"
              data-testid="admin-forgot-use-otp-btn"
            >
              Or email me a one-time OTP instead
            </button>
          </form>
        )}

        {otpMode && !otpSent && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Phone Number
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter admin phone number"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="admin-forgot-otp-phone-input"
                  required
                />
              </div>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
              data-testid="admin-forgot-send-otp-btn"
            >
              {loading ? 'Sending OTP...' : 'Send OTP to registered email'}
            </button>
            <button
              type="button"
              onClick={() => { setError(''); setOtpMode(false); }}
              className="w-full text-center text-sm text-gray-600 hover:underline"
            >
              Back to Google verification
            </button>
          </div>
        )}

        {otpMode && otpSent && (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            {otpEmailHint && (
              <div className="bg-purple-50 border border-purple-200 text-purple-800 px-3 py-2 rounded-lg text-xs">
                OTP sent to <strong>{otpEmailHint}</strong>. Valid for 10 minutes.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">6-digit OTP</label>
              <input
                type="text" inputMode="numeric" maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full text-center tracking-[0.4em] text-xl font-mono py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                data-testid="admin-forgot-otp-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                data-testid="admin-forgot-new-password-input"
                required
              />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
              data-testid="admin-forgot-reset-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminForgotPassword;
